import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { QuoteService } from '@lx/quote-tool/shared/data-access/quote';
import { LoggerService, parseApiError } from '@lx/shared/logger';
import { catchError, debounceTime, filter, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { OnlineQuoteToolStore } from '../store';
import { QuoteStore } from '../store';
import { OnlineQuoteInputMapper } from './quote-input.mapper';

/**
 * Service to handle automatic price calculation based on course selection changes.
 *
 * This service:
 * - Listens to store state changes
 * - Triggers price calculation when all required fields are present
 * - Debounces API calls to prevent excessive requests
 * - Updates store with calculated prices
 * - Handles errors gracefully
 */
@Injectable({
  providedIn: 'root',
})
export class PriceCalculationService {
  private readonly store = inject(OnlineQuoteToolStore);
  private readonly quoteStore = inject(QuoteStore);
  private readonly quoteService = inject(QuoteService);
  private readonly quoteInputMapper = inject(OnlineQuoteInputMapper);
  private readonly loggerService = inject(LoggerService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly DEBOUNCE_TIME_MS = 300;

  /**
   * Initializes automatic price calculation on selection changes.
   * It is called on AppBootstrapService.bootstrapUiBehaviors() to start listening for state changes.
   *
   * The subscription is automatically cleaned up when the component/service is destroyed.
   */
  initializePriceCalculation(schoolCode: string): void {
    if (!schoolCode) {
      this.loggerService.error('Cannot initialize price calculation without school code');
      return;
    }

    this.store.selectionForPriceCalculation$
      .pipe(
        // Only trigger if all required fields are present
        filter(({ selectedCourse, selectedDuration, selectedStartDate }) => {
          const allFieldsPresent = !!selectedCourse && !!selectedDuration && !!selectedStartDate;

          if (!allFieldsPresent) {
            this.loggerService.info('Price calculation skipped - missing required fields');
          }

          return allFieldsPresent;
        }),
        // Debounce to avoid excessive API calls when user is rapidly changing selections
        debounceTime(this.DEBOUNCE_TIME_MS),
        // Map selection to quote input and call API
        switchMap((selection) => {
          const quoteInput = this.quoteInputMapper.mapQuoteInput(selection);

          if (!quoteInput) {
            this.loggerService.error('Failed to map quote input for price calculation');
            return of(null);
          }

          return this.quoteService.getQuotePriceV2(quoteInput).pipe(
            catchError((error) => {
              this.loggerService.error('Price calculation failed', parseApiError(error));
              // Return null to continue the stream without breaking
              return of(null);
            }),
          );
        }),
        // Auto cleanup on destroy
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((quoteOutput) => {
        // Update store with pricing results
        if (quoteOutput?.grandTotal && quoteOutput.grandTotal.length > 0) {
          const { grandTotal, displayCurrency, destinationCurrency } = quoteOutput;
          this.store.updatePricing({ grandTotal, currency: displayCurrency ?? destinationCurrency });
          this.quoteStore.updateState({ ...quoteOutput });
        }
      });
  }
}
